import Box from '@/common/components/Box'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import MaintenanceObjective from '@/common/components/MaintenanceObjective'
import ResumeItem from '@/common/components/ResumeItem'
import { formatCurrency, formatUnit } from '@/common/utils/formatters'
import { WrenchIcon } from 'lucide-react'
import { useMemo } from 'react'
import ProposalAwareResumeItem from '../ProposalAwareResumeItem'
import {
  ManualEntryProposalContent,
  ManualEntryProposalExecutionObjective,
  ManualEntryProposalModification,
} from '../../types/maintenanceManualEntryProposal.type'

type Props = {
  objectives: ManualEntryProposalExecutionObjective[]
  content: ManualEntryProposalContent | null
}

const toMap = (modifications: ManualEntryProposalModification[] | undefined, key: 'objective_piece_id' | 'objective_activity_id') => {
  const map = new Map<string, ManualEntryProposalModification>()
  modifications?.forEach((modification) => {
    const id = modification[key]
    if (id) map.set(id, modification)
  })
  return map
}

const CountLine = ({ count, label, total }: { count: number; label: string; total: number }) => (
  <div className="flex items-center gap-2">
    <p className="font-bold text-base leading-none">{label}</p>
    <div className="flex items-center gap-0.5 text-xs leading-none">
      <span>{count}</span>
      <span>•</span>
      <span className="text-valid">{formatCurrency(total)}</span>
    </div>
  </div>
)

const ProposalObjectives = ({ objectives, content }: Props) => {
  const pieceModifications = useMemo(() => toMap(content?.piece_modifications, 'objective_piece_id'), [content])
  const activityModifications = useMemo(() => toMap(content?.activity_modifications, 'objective_activity_id'), [content])

  return (
    <div className="flex flex-col gap-3">
      {objectives.map((objective) => (
        <Box key={objective.id} className="flex flex-col gap-4 p-4 rounded-lg">
          <MaintenanceObjective
            systemDisplayName={objective.system?.display_name}
            actionDisplayName={objective.action?.display_name}
            partDisplayName={objective.part?.display_name}
            actionDescription={objective.action?.description}
          />

          {objective.execution_pieces.length > 0 && (
            <div className="flex flex-col gap-3">
              <CountLine
                label="Peças"
                count={objective.execution_pieces.length}
                total={objective.execution_pieces.reduce((sum, piece) => {
                  const modification = pieceModifications.get(piece.id)
                  return sum + Number(modification?.new_total_value ?? piece.total_value)
                }, 0)}
              />

              {objective.execution_pieces.map((piece) => {
                const modification = pieceModifications.get(piece.id)

                return (
                  <Box key={piece.id} className="p-4">
                    <div className="flex gap-4">
                      <ImageCarousel
                        files={(piece.piece?.images || [])
                          .filter((image) => !!image?.url)
                          .map((image) => ({ id: image.url as string, file: image.url as string }))}
                        Placeholder={
                          <ImageWithPlaceholder className="size-20 rounded-md" iconClassName="size-10" placeholderIcon={WrenchIcon} />
                        }
                        className="size-20"
                        showExpandButton
                        showNavigationButtons={false}
                        showRemoveButton={false}
                      />

                      <div className="flex flex-col gap-2">
                        <p className="text-base font-bold">{piece.piece?.display_name || '--'}</p>

                        <div className="flex items-center flex-wrap gap-3">
                          <ResumeItem
                            className="text-sm"
                            label="Valor unt."
                            value={formatCurrency(modification?.new_unit_price != null ? Number(modification.new_unit_price) : Number(piece.unit_price) || 0)}
                          />

                          <p className="text-sm">x</p>

                          <ResumeItem className="text-sm" label="Qntd" value={formatUnit(piece.quantity, 'unit')} />

                          <p className="text-sm">=</p>

                          <ProposalAwareResumeItem
                            label="Valor total"
                            originalValue={Number(modification?.original_total_value ?? piece.total_value) || 0}
                            newValue={modification?.new_total_value != null ? Number(modification.new_total_value) : null}
                            differencePercentage={modification?.difference_percentage}
                            format={formatCurrency}
                          />

                          <ProposalAwareResumeItem
                            label="Garantia"
                            originalValue={piece.warranty_in_days}
                            newValue={modification?.new_warranty_in_days}
                            format={(value) => `${value} Dias`}
                            higherIsBetter
                          />

                          <ResumeItem className="text-sm" label="Código da peça" value={piece.piece?.number || '--'} />
                        </div>

                        <ResumeItem className="text-sm" label="Descrição" value={piece.description} />
                      </div>
                    </div>
                  </Box>
                )
              })}
            </div>
          )}

          {objective.execution_activities.length > 0 && (
            <div className="flex flex-col gap-3">
              <CountLine
                label="Serviços"
                count={objective.execution_activities.length}
                total={objective.execution_activities.reduce((sum, activity) => {
                  const modification = activityModifications.get(activity.id)
                  return sum + Number(modification?.new_total_value ?? activity.total_value)
                }, 0)}
              />

              {objective.execution_activities.map((activity) => {
                const modification = activityModifications.get(activity.id)

                return (
                  <Box key={activity.id} className="p-4">
                    <div className="flex gap-4">
                      <ImageCarousel
                        files={(activity.images || [])
                          .filter((image) => !!image?.url)
                          .map((image) => ({ id: image.url as string, file: image.url as string }))}
                        Placeholder={
                          <ImageWithPlaceholder className="size-20 rounded-md" iconClassName="size-10" placeholderIcon={WrenchIcon} />
                        }
                        className="size-20"
                        showExpandButton
                        showNavigationButtons={false}
                        showRemoveButton={false}
                      />

                      <div className="flex flex-col gap-2">
                        <p className="text-base font-bold">{activity.display_name || '--'}</p>

                        <div className="flex items-center flex-wrap gap-3">
                        <ResumeItem
                          className="text-sm"
                          label="Valor unt."
                          value={formatCurrency(modification?.new_unit_price != null ? Number(modification.new_unit_price) : Number(activity.unit_price) || 0)}
                        />

                        <p className="text-sm">x</p>

                        <ResumeItem
                          className="text-sm"
                          label={activity.unit_for_quantity === 'minutes' ? 'Tempo de execução' : 'Unidades'}
                          value={formatUnit(activity.quantity, activity.unit_for_quantity === 'minutes' ? 'hours' : 'unit')}
                        />

                        <p className="text-sm">=</p>

                        <ProposalAwareResumeItem
                          label="Valor total"
                          originalValue={Number(modification?.original_total_value ?? activity.total_value) || 0}
                          newValue={modification?.new_total_value != null ? Number(modification.new_total_value) : null}
                          differencePercentage={modification?.difference_percentage}
                          format={formatCurrency}
                        />

                        <ProposalAwareResumeItem
                          label="Garantia"
                          originalValue={activity.warranty_in_days}
                          newValue={modification?.new_warranty_in_days}
                          format={(value) => `${value} Dias`}
                          higherIsBetter
                        />
                      </div>

                        <ResumeItem className="text-sm" label="Descrição" value={activity.description} />
                      </div>
                    </div>
                  </Box>
                )
              })}
            </div>
          )}
        </Box>
      ))}
    </div>
  )
}

export default ProposalObjectives
